/* LoginForm.js */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../AuthContext'; // Import du contexte
import apiUrl from '../config';
import '../CSS/Login.css';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useLocation } from 'react-router-dom';



const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [mot_de_passe, setMotDePasse] = useState('');
  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuth();  // Récupérer isAuthenticated et login depuis le contexte
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [openForgotDialog, setOpenForgotDialog] = useState(false);
const [forgotEmail, setForgotEmail] = useState('');
const [loadingForgotPassword, setLoadingForgotPassword] = useState(false);
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";  // Récupère la page d'origine
  
  useEffect(() => {
    if (!loadingLogin && isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, loadingLogin, navigate, from]);
  
  

  const handleSubmit = async (e) => {
  e.preventDefault();

  if (!email.trim() || !mot_de_passe.trim()) {
    setNotification({
      open: true,
      message: 'Veuillez renseigner votre email et votre mot de passe.',
      severity: 'error',
    });
    return;
  }

  setLoadingLogin(true);

  try {
    const res = await axios.post(`${apiUrl}/login`, {
      email: email.trim().toLowerCase(),
      mot_de_passe
    });

    if (res.data.token) {
      login(res.data.role, res.data.token);

      setNotification({
        open: true,
        message: 'Connexion réussie.',
        severity: 'success',
      });

      navigate(from, { replace: true });
    } else {
      throw new Error('Token absent dans la réponse');
    }
  } catch (error) {
    console.error('Login error:', error.response ? error.response.data : error.message);
    setNotification({
      open: true,
      message: 'Email ou mot de passe incorrect.',
      severity: 'error',
    });
  } finally {
    setLoadingLogin(false);
  }
};

const handleForgotPassword = async () => {
  const emailToSend = forgotEmail.trim().toLowerCase();

  if (!emailToSend) {
    setNotification({
      open: true,
      message: 'Veuillez renseigner votre email.',
      severity: 'error',
    });
    return;
  }

  setLoadingForgotPassword(true);

  try {
    await axios.post(`${apiUrl}/forgot-password`, {
      email: emailToSend
    });

    setNotification({
      open: true,
      message: 'Si un compte existe avec cet email, un lien de réinitialisation a été envoyé.',
      severity: 'success',
    });

    setOpenForgotDialog(false);
    setForgotEmail('');

  } catch (error) {
    setNotification({
      open: true,
      message: error.response?.data?.message || "Erreur lors de l'envoi du lien.",
      severity: 'error',
    });

  } finally {
    setLoadingForgotPassword(false);
  }
};
  

 return (
  <Container maxWidth="sm" className="login-container">
    <div className="login-logo-container">
      <img
        src="/BIAM Consulting Main Logo.png"
        alt="Logo Nova Portage"
        className="login-logo"
      />
    </div>

    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Connexion
      </Typography>

      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Email"
          margin="normal"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        <TextField
          fullWidth
          label="Mot de passe"
          type="password"
          margin="normal"
          value={mot_de_passe}
          onChange={(e) => setMotDePasse(e.target.value)}
          required
        />

        {/* ✅ ICI : bouton mot de passe oublié */}
        <Button
          type="button"
          className="forgot-password-link"
          onClick={() => {
            setForgotEmail(email);
            setOpenForgotDialog(true);
          }}
        >
          Mot de passe oublié ?
        </Button>

        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={loadingLogin}
        >
          {loadingLogin ? 'Connexion en cours...' : 'Se connecter'}
        </Button>
      </form>
    </Box>

    {/* ✅ ICI : popup mot de passe oublié */}
    <Dialog
      open={openForgotDialog}
      onClose={() => !loadingForgotPassword && setOpenForgotDialog(false)}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle sx={{ fontWeight: 'bold', color: '#0f3f63', textAlign: 'center' }}>
        Mot de passe oublié
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ mb: 2, color: '#475569', fontSize: '14px' }}>
          Entrez votre email. Vous recevrez un lien pour créer un nouveau mot de passe.
        </Typography>

        <TextField
          fullWidth
          label="Email"
          type="email"
          value={forgotEmail}
          onChange={(e) => setForgotEmail(e.target.value)}
          margin="normal"
          required
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          type="button"
          onClick={() => setOpenForgotDialog(false)}
          disabled={loadingForgotPassword}
          variant="outlined"
          fullWidth
        >
          Annuler
        </Button>

        <Button
          type="button"
          onClick={handleForgotPassword}
          disabled={loadingForgotPassword}
          variant="contained"
          fullWidth
        >
          {loadingForgotPassword ? 'Envoi...' : 'Envoyer le lien'}
        </Button>
      </DialogActions>
    </Dialog>

    <Snackbar
      open={notification.open}
      autoHideDuration={3000}
      onClose={() => setNotification({ ...notification, open: false })}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert
        severity={notification.severity}
        sx={{ width: '100%', fontWeight: 'bold', textAlign: 'center' }}
      >
        {notification.message}
      </Alert>
    </Snackbar>
  </Container>
);
};

export default LoginForm;
